/** Invitation UI uses canonical account state and verified server transitions.
 * Previewing, signup and claiming attribution never imply friendship acceptance. */
export function createReferrals(ctx, handoff) {
  let generation = 0;
  const text = (key, values = {}) => ctx.translate(`referral.${key}`, values);
  const actor = () => ctx.state?.me?.id ?? null;
  const statusNode = () => {
    const node = ctx.e('p', 'subtle');
    node.setAttribute('role', 'status');
    return node;
  };
  function openInvite() {
    const owner = actor(),
      ownerRoute = ctx.route()[0],
      current = ++generation;
    if (!owner) return;
    let disposed = false,
      pending = false,
      requestId;
    const active = () =>
      !disposed && actor() === owner && ctx.route()[0] === ownerRoute && current === generation;
    const body = ctx.e('form', 'referral-review form-stack');
    const email = ctx.field('input', {
      type: 'email',
      required: true,
      maxLength: 254,
      ariaLabel: text('email'),
      placeholder: text('email'),
    });
    const country = ctx.field('input', { type: 'checkbox', required: true });
    const confirm = ctx.field('input', { type: 'checkbox', required: true });
    const countryLabel = ctx.append(
      ctx.e('label', 'referral-check'),
      country,
      ctx.e('span', '', text('countryReview')),
    );
    const confirmLabel = ctx.append(
      ctx.e('label', 'referral-check'),
      confirm,
      ctx.e('span', '', text('connectReview')),
    );
    const notice = statusNode();
    const send = ctx.e('button', 'primary-button', text('send'));
    send.disabled = true;
    const receipt = ctx.e('div', 'referral-receipt');
    const history = ctx.e('div', 'referral-history');
    body.append(
      ctx.e('p', '', text('preview', { name: ctx.state.me.name })),
      email,
      countryLabel,
      confirmLabel,
      ctx.e('p', 'subtle', text('verificationNotice')),
      send,
      notice,
      receipt,
      history,
    );
    ctx.modal.addEventListener(
      'close',
      () => {
        disposed = true;
        email.value = '';
        receipt.replaceChildren();
        history.replaceChildren();
      },
      { once: true },
    );
    ctx.dialog(text('invite'), body);
    void ctx
      .api('/api/referrals')
      .then((data) => {
        if (!active()) return;
        for (const item of data.invitations ?? []) {
          const row = ctx.e('div', 'referral-history-row');
          const label = item.recipientEmail ?? text('hiddenRecipient');
          const details = ctx.e(
            'span',
            '',
            `${label} · ${new Date(item.createdAt).toLocaleDateString()} · ${text(item.status + 'Status')}`,
          );
          row.append(details);
          if (item.status !== 'accepted' && item.status !== 'cancelled') {
            const cancel = ctx.button(text('cancel'), async () => {
              cancel.disabled = true;
              try {
                const result = await ctx.api('/api/referrals', { action: 'cancel', id: item.id });
                if (active()) {
                  details.textContent = `${label} · ${text(result.status === 'accepted' ? 'acceptedStatus' : 'cancelledStatus')}`;
                  cancel.remove();
                }
              } catch (error) {
                if (active()) {
                  notice.textContent = error.message;
                  cancel.disabled = false;
                }
              }
            });
            row.append(cancel);
          }
          history.append(row);
        }
        send.disabled = !data.available || ctx.state.account?.verificationRequired === true;
        if (send.disabled)
          notice.textContent = text(
            ctx.state.account?.verificationRequired ? 'verifyFirst' : 'unavailable',
          );
      })
      .catch(() => {
        if (active()) notice.textContent = text('unavailable');
      });
    email.addEventListener('input', () => {
      if (!pending) requestId = undefined;
    });
    body.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (pending || !active() || send.disabled || !body.reportValidity()) return;
      pending = true;
      send.disabled = true;
      requestId ??= crypto.randomUUID();
      const submittedEmail = email.value.trim();
      notice.textContent = text('submitting');
      try {
        const result = await ctx.api('/api/referrals', {
          action: 'create',
          email: submittedEmail,
          requestId,
          reviewed: true,
          connect: true,
          recipientCountry: 'US',
        });
        if (!active()) return;
        notice.textContent = text('submitted');
        email.disabled = true;
        country.disabled = true;
        confirm.disabled = true;
        const cancel = ctx.button(text('cancel'), async () => {
          cancel.disabled = true;
          try {
            const cancellation = await ctx.api('/api/referrals', {
              action: 'cancel',
              id: result.id,
            });
            if (active()) {
              notice.textContent = text(
                cancellation.status === 'accepted' ? 'acceptedStatus' : 'cancelled',
              );
              receipt.replaceChildren();
            }
          } catch (error) {
            if (active()) {
              notice.textContent = error.message;
              cancel.disabled = false;
            }
          }
        });
        receipt.append(ctx.e('p', '', submittedEmail), cancel);
      } catch (error) {
        if (active()) {
          notice.textContent = error.message;
          send.disabled = false;
        }
      } finally {
        pending = false;
      }
    });
  }
  async function renderRecipient(host = ctx.view) {
    const current = ++generation,
      owner = actor(),
      route = ctx.route()[0],
      value = handoff.read();
    const active = () => current === generation && actor() === owner && ctx.route()[0] === route;
    const panel = ctx.e('section', 'referral-welcome panel');
    const status = statusNode();
    panel.append(
      ctx.e('h1', '', text(value?.kind === 'stop-invitations' ? 'stopTitle' : 'welcome')),
      status,
    );
    host.append(panel);
    const exits = ctx.e('div', 'button-row referral-exits');
    const leave = ctx.button(text('leave'), () => {
      if (!active()) return;
      handoff.clear();
      ctx.go('feed');
    });
    exits.append(leave);
    if (owner) {
      const changeAccount = ctx.button(text('changeAccount'), async () => {
        if (!active()) return;
        changeAccount.disabled = true;
        try {
          await ctx.signOut();
        } catch (error) {
          if (active()) {
            status.textContent = error.message;
            changeAccount.disabled = false;
          }
        }
      });
      exits.append(changeAccount);
    }
    panel.append(exits);
    if (!value) {
      status.textContent = text('expired');
      return;
    }
    if (value.kind === 'stop-invitations') {
      status.textContent = text('stopNotice');
      const stop = ctx.button(text('stopAction'), async () => {
        stop.disabled = true;
        try {
          await ctx.api('/api/referrals', { action: 'optout', token: value.token });
          if (active()) {
            handoff.clear();
            status.textContent = text('stopped');
            stop.remove();
            leave.textContent = text('continue');
          }
        } catch (error) {
          if (active()) {
            status.textContent = error.message;
            stop.disabled = false;
          }
        }
      });
      panel.append(stop);
      return;
    }
    status.textContent = text('loading');
    try {
      const preview = await ctx.api('/api/referrals', { action: 'preview', token: value.token });
      if (!active()) return;
      panel.querySelector('h1').textContent = text('personalWelcome', {
        name: preview.inviter.name,
      });
      status.textContent = text(owner ? 'acceptNotice' : 'signupNotice');
      if (!owner) return;
      if (ctx.state.account?.verificationRequired) {
        panel.append(ctx.link(text('verifyFirst'), '#/verify-email', 'primary-button'));
        return;
      }
      await ctx.api('/api/referrals', { action: 'claim', token: value.token });
      if (!active()) return;
      const actions = ctx.e('div', 'button-row');
      let pending = false;
      for (const action of ['accept', 'decline']) {
        const control = ctx.button(text(action), async () => {
          if (pending || !active()) return;
          pending = true;
          for (const child of actions.children) child.disabled = true;
          try {
            await ctx.api('/api/referrals', { action, token: value.token });
            if (!active()) return;
            handoff.clear();
            actions.replaceChildren();
            status.textContent = text(action === 'accept' ? 'accepted' : 'declined');
            leave.textContent = text('continue');
            exits.replaceChildren(leave);
            if (action === 'accept') {
              try {
                const snapshot = await ctx.api('/api/state');
                if (active()) ctx.state = snapshot;
              } catch {
                if (active()) {
                  status.textContent = text('acceptedRefreshFailed');
                  const retry = ctx.button(text('refresh'), async () => {
                    retry.disabled = true;
                    try {
                      const snapshot = await ctx.api('/api/state');
                      if (active()) {
                        ctx.state = snapshot;
                        status.textContent = text('accepted');
                        retry.remove();
                      }
                    } catch {
                      if (active()) {
                        status.textContent = text('acceptedRefreshFailed');
                        retry.disabled = false;
                      }
                    }
                  });
                  exits.append(retry);
                }
              }
            }
          } catch (error) {
            if (active()) {
              status.textContent = error.message;
              for (const child of actions.children) child.disabled = false;
            }
          } finally {
            pending = false;
          }
        });
        actions.append(control);
      }
      panel.append(actions);
    } catch (error) {
      if (active()) status.textContent = error.message;
    }
  }
  function mountPublic() {
    document.querySelector('#referral-public')?.remove();
    if (!handoff.read()) return;
    const host = ctx.e('div');
    host.id = 'referral-public';
    document.querySelector('#login').prepend(host);
    void renderRecipient(host);
  }
  ctx.openInvite = openInvite;
  ctx.referralDestination = () => handoff.destination(ctx.state?.account);
  return {
    openInvite,
    renderRecipient,
    mountPublic,
    dispose: () => {
      generation++;
    },
  };
}
